/**
 * InterviewInput Component
 *
 * Sticky mobile input with expandable editor and collapsible interview controls.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  ChevronDown,
  Expand,
  FileText,
  Minimize2,
  RefreshCw,
  RotateCcw,
  Send,
} from 'lucide-react';

export default function InterviewInput({
  input,
  onInputChange,
  onSend,
  onStartNew,
  onRestart,
  onFinish,
  isLoading,
  isFinished,
  isBootstrapping,
  canFinish,
  textareaRef,
  currentQuestionNumber,
  totalQuestions,
  currentQuestionText,
  showDraftSaved,
}) {
  const [showControls, setShowControls] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const expandedTextareaRef = useRef(null);

  const canSend = Boolean(input.trim()) && !isLoading && !isFinished && !isBootstrapping;
  const isInputDisabled = isLoading || isFinished || isBootstrapping;

  const wordCount = useMemo(() => {
    const normalized = input.trim();
    if (!normalized) return 0;
    return normalized.split(/\s+/).length;
  }, [input]);

  useEffect(() => {
    if (!isExpanded) return;
    const timer = window.setTimeout(() => {
      expandedTextareaRef.current?.focus();
    }, 60);
    return () => window.clearTimeout(timer);
  }, [isExpanded]);

  const renderMeta = () => (
    <div className="flex items-center justify-between px-1 pt-1.5">
      <span className="text-[11px] text-[#B0A8A0]">{wordCount > 0 ? `${wordCount} words` : ''}</span>
      {showDraftSaved && (
        <span className="inline-flex items-center gap-1 text-[11px] text-[#5FA847]">
          <Check className="h-3.5 w-3.5" />
          Draft saved
        </span>
      )}
    </div>
  );

  return (
    <>
      <footer className="fixed bottom-0 left-0 right-0 z-40 border-t border-[#EFE7DF] bg-white/95 backdrop-blur">
        <div
          className="mx-auto w-full max-w-[740px] px-3 pb-2 pt-2"
          style={{ paddingBottom: 'max(8px, env(safe-area-inset-bottom))' }}
        >
          {!isFinished && (
            <section className="mb-2 overflow-hidden rounded-xl border border-[#EFE7DF] bg-white">
              <button
                type="button"
                onClick={() => setShowControls((prev) => !prev)}
                className="flex w-full items-center gap-2 px-3 py-2.5 text-left"
              >
                <span className="text-xs font-semibold text-[#2D2A26]">Interview Controls</span>
                <span
                  className={`ml-auto text-[#B0A8A0] transition-transform duration-300 ${
                    showControls ? 'rotate-180' : ''
                  }`}
                >
                  <ChevronDown className="h-4 w-4" />
                </span>
              </button>

              <div
                className="grid transition-[grid-template-rows,opacity] duration-300 ease-out"
                style={{ gridTemplateRows: showControls ? '1fr' : '0fr', opacity: showControls ? 1 : 0 }}
              >
                <div className="overflow-hidden">
                  <div className="space-y-2 px-3 pb-3">
                    <button
                      type="button"
                      onClick={onStartNew}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#E8732A] px-3 py-2 text-xs font-semibold text-white disabled:opacity-60"
                      disabled={isLoading}
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      Start New Interview
                    </button>
                    <button
                      type="button"
                      onClick={onRestart}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#FFF3E8] px-3 py-2 text-xs font-semibold text-[#E8732A] disabled:opacity-60"
                      disabled={isLoading}
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Restart Interview
                    </button>
                    <button
                      type="button"
                      onClick={onFinish}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#F5F0EB] px-3 py-2 text-xs font-semibold text-[#6A6560] disabled:opacity-60"
                      disabled={isLoading || !canFinish}
                    >
                      <FileText className="h-3.5 w-3.5" />
                      Finish & Review Now
                    </button>
                  </div>
                </div>
              </div>
            </section>
          )}

          {isFinished && (
            <div className="mb-2 flex justify-center">
              <button
                type="button"
                onClick={onStartNew}
                className="inline-flex items-center gap-2 rounded-xl bg-[#E8732A] px-5 py-2.5 text-sm font-semibold text-white"
                disabled={isLoading}
              >
                <RefreshCw className="h-4 w-4" />
                Start New Interview
              </button>
            </div>
          )}

          <div className="mb-1.5 inline-flex items-center gap-2 text-[11px] font-semibold text-[#E8732A]">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#E8732A] text-[10px] text-white">
              {currentQuestionNumber}
            </span>
            Answer question {currentQuestionNumber} of {totalQuestions}
          </div>

          <div className="rounded-2xl bg-[#F5F0EB] px-3 py-2 transition-shadow duration-150 focus-within:shadow-[0_0_0_2px_rgba(232,115,42,0.25)]">
            <div className="flex items-end gap-2">
              <textarea
                ref={textareaRef}
                rows={1}
                placeholder={isFinished ? 'Interview finished' : 'Type your answer...'}
                className="min-h-[40px] max-h-[120px] flex-1 resize-none bg-transparent text-sm leading-6 text-[#2D2A26] outline-none"
                style={{ maxHeight: '120px' }}
                value={input}
                onChange={onInputChange}
                disabled={isInputDisabled}
              />

              <button
                type="button"
                onClick={() => setIsExpanded(true)}
                className="flex h-9 w-9 items-center justify-center rounded-full text-[#8A8580] transition hover:bg-white/60 disabled:opacity-50"
                disabled={isInputDisabled}
                title="Expand editor"
              >
                <Expand className="h-4 w-4" />
              </button>

              <button
                type="button"
                onClick={onSend}
                disabled={!canSend}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-[#E8732A] text-white transition disabled:cursor-not-allowed disabled:bg-[#D8D1C8]"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>

            {renderMeta()}
          </div>
        </div>
      </footer>

      {isExpanded && (
        <div className="fixed inset-0 z-[90] mx-auto flex h-full w-full max-w-[740px] flex-col bg-[#FAF8F5]">
          <header className="flex items-center justify-between border-b border-[#EFE7DF] bg-white px-4 py-3">
            <h3 className="text-sm font-semibold text-[#2D2A26]">Your Answer - Q{currentQuestionNumber}</h3>
            <button
              type="button"
              onClick={() => setIsExpanded(false)}
              className="flex h-9 w-9 items-center justify-center rounded-full text-[#E8732A] hover:bg-[#FFF3E8]"
            >
              <Minimize2 className="h-4 w-4" />
            </button>
          </header>

          <section className="border-b border-[#EFE7DF] bg-[#FFF8F0] px-4 py-3 text-[13px] leading-6 text-[#5A5550]">
            <span className="font-semibold text-[#E8732A]">Question {currentQuestionNumber}:</span>{' '}
            {currentQuestionText || 'Waiting for the next question...'}
          </section>

          <div className="flex-1 px-4 py-3">
            <textarea
              ref={expandedTextareaRef}
              value={input}
              onChange={onInputChange}
              disabled={isInputDisabled}
              placeholder="Write your detailed answer here..."
              className="h-full w-full min-h-[220px] resize-none rounded-2xl border border-[#E8E3DE] bg-white p-3 text-sm leading-6 text-[#2D2A26] outline-none transition focus:border-[#E8732A] focus:ring-2 focus:ring-[#F6CFB6]"
            />
            {renderMeta()}
          </div>

          <div
            className="grid grid-cols-3 gap-2 border-t border-[#EFE7DF] bg-white px-4 py-3"
            style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}
          >
            <button
              type="button"
              onClick={() => setIsExpanded(false)}
              className="rounded-xl bg-[#F5F0EB] px-3 py-2.5 text-sm font-semibold text-[#6A6560]"
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => {
                setIsExpanded(false);
                onSend();
              }}
              disabled={!canSend}
              className="col-span-2 inline-flex items-center justify-center gap-2 rounded-xl bg-[#E8732A] px-3 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#D8D1C8]"
            >
              <Send className="h-4 w-4" />
              Send Answer
            </button>
          </div>
        </div>
      )}
    </>
  );
}
